import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { PaymentContainer } from 'src/components/PaymentContainer/PaymentContainer';
import { PaymentGenericError } from 'src/components/PaymentGenericError/PaymentGenericError';
import { GetCheckoutPaymentPageDocument } from 'src/generated/graphql';
import useOverflowScroll from 'src/hooks/useOverflowScroll';
import { isNonNullable } from 'src/utils';
import { validateListingProducts } from 'src/utils/listingProducts';

import { PaymentSingleProduct } from './components/PaymentSingleProduct';
import { PaymentWithSecondProduct } from './components/PaymentWithSecondProduct';

export const PaymentPage = () => {
  useOverflowScroll();
  const [searchParams] = useSearchParams();
  const productTokens = searchParams.getAll('productToken');
  const listingId = searchParams.get('listingId');

  const { data, error, loading } = useQuery(GetCheckoutPaymentPageDocument);

  if (loading) {
    return (
      <PaymentContainer width="medium">
        <CustomLoader />
      </PaymentContainer>
    );
  }

  // TODO unit test
  if (!data || error) {
    return <PaymentGenericError testId="basic-data-fetching-failure-error" />;
  }

  const client = data.user?.userView?.client;
  const listings = data.user?.userView?.client?.listings;
  const validatedProducts = validateListingProducts(data.listingProducts);

  // TODO unit test
  if (!client) {
    return <PaymentGenericError testId="client-validation-error" />;
  }

  // TODO unit test
  if (validatedProducts.kind === 'error') {
    return <PaymentGenericError testId="product-validation-error" />;
  }

  const selectedProducts = productTokens
    .map((token) =>
      data.listingProducts.find((product) => product.productToken === token),
    )
    .filter(isNonNullable);

  // Arrays have no fixed size so we cannot make guarantees about the items at
  // certain indices. Pulling them out allows us to do this
  const firstUrlProduct = selectedProducts.at(0);
  const secondUrlProduct = selectedProducts.at(1);

  // TODO unit test
  // We only support one or two products in the checkout flow
  if (!firstUrlProduct || selectedProducts.length > 2) {
    return <PaymentGenericError testId="product-quantities-error" />;
  }

  const isListingSearchBoosted =
    listingId && listings
      ? listings.some(
          (listing) => listingId === String(listing.id) && listing.isPremium,
        )
      : false;

  if (secondUrlProduct) {
    return (
      <PaymentWithSecondProduct
        clientId={client.id}
        firstUrlProduct={firstUrlProduct}
        secondUrlProduct={secondUrlProduct}
        singleProducts={validatedProducts.singleProducts}
        combinedProducts={validatedProducts.combinedProducts}
        listingId={listingId}
      />
    );
  }

  return (
    <PaymentSingleProduct
      clientId={client.id}
      firstUrlProduct={firstUrlProduct}
      singleProducts={validatedProducts.singleProducts}
      combinedProducts={validatedProducts.combinedProducts}
      listingId={listingId}
      isListingSearchBoosted={isListingSearchBoosted}
    />
  );
};
